import { FC, useMemo } from 'react';
import { css } from '@emotion/react';
import {
  AddOn,
  PackageType,
  SetDemoLocationCustomizationsRequest,
} from '@weave/schema-gen-ts/dist/schemas/package-service/v1/package.pb';
import { OpsType } from '@weave/schema-gen-ts/dist/shared/organization/ops_type.pb';
import { Vertical } from '@weave/schema-gen-ts/dist/shared/vertical/vertical.pb';
import { useHotkeys } from 'react-hotkeys-hook';
import { SubscriptionsQueries } from '@frontend/api-subscription';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { sentry } from '@frontend/tracking';
import { generateEnumOptions } from '@frontend/types';
import { reloadPage } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import {
  CheckboxField,
  ChecklistField,
  ContentLoader,
  DropdownField,
  Modal,
  MultiselectField,
  RadioCardField,
  Text,
  useForm,
  useModalControl,
  useAlert,
} from '@frontend/design-system';
import {
  BundleAddOnsEnum,
  BundleAddOnsLabel,
  FeatureAddOnsLabel,
  IntegrationPackagesEnum,
  IntegrationPackagesLabel,
  computeDemoBundle,
  computeDemoAddOns,
  LegacyBundlePackagesLabel,
  LegacyBundlePackagesEnum,
  getLegacySalesforceBundlePackage,
  LegacyBundleOptionalsEnum,
} from '../helpers/salesforce-bundle';

const DEMO_TOOL_HOTKEY = 'ctrl+shift+s';
const ALLOWED_LOCATION_TYPE_FOR_DEMO: OpsType[] = [OpsType.DEMO];
const integrationPackageOptions = Object.values(IntegrationPackagesEnum).map((value) => ({
  value,
  label: IntegrationPackagesLabel[value],
}));
const bundleAddOnOptions = [BundleAddOnsEnum.COORDINATE, BundleAddOnsEnum.ATTRACT].map((value) => ({
  value,
  label: BundleAddOnsLabel[value],
}));
const featureAddOnOptions = [AddOn.CALL_INTELLIGENCE, AddOn.PRACTICE_ANALYTICS, AddOn.WIV].map((value) => ({
  value,
  label: FeatureAddOnsLabel[value] ?? value,
}));

const verticalOptions = generateEnumOptions(Vertical, [Vertical.INVALID_VERTICAL]);
export const NEW_BUNDLE_DEMO_FF = 'sales-demo:new-bundle-structure';

export const SalesDemoFeatureModal: FC = () => {
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isNewBundleFFEnabled = getFeatureFlagValue(NEW_BUNDLE_DEMO_FF);
  return isNewBundleFFEnabled ? <NewBundleDemoModal /> : <LegacySalesDemoFeatureModal />;
};

const NewBundleDemoModal: FC = () => {
  const { t } = useTranslation('salesDev');
  const alerts = useAlert();
  const { modalProps, openModal, closeModal } = useModalControl();
  const { accessibleLocationData, selectedLocationIds } = useAppScopeStore();

  const filteredLocationIds = useMemo(() => {
    return selectedLocationIds.filter((id) => {
      const opsType = accessibleLocationData[id]?.opsType;
      return !!opsType && ALLOWED_LOCATION_TYPE_FOR_DEMO.includes(opsType as OpsType);
    });
  }, [accessibleLocationData, selectedLocationIds]);

  const { mutate, isLoading } = SubscriptionsQueries.useUpdateDemoLocationCustomization({
    onSuccess: () => {
      closeModal();
      reloadPage();
    },
    onError: (error) => {
      sentry.error({
        team: 'fe-clm',
        topic: 'customer-lifecycle-management',
        error: `Failed to update demo location customizations: ${error}`,
        addContext: {
          name: 'UpdateDemoLocationCustomizationsError',
          context: {
            locationIds: filteredLocationIds,
            error,
          },
        },
      });
      alerts.error(t('Failed to update demo location customizations. Please try again'));
    },
  });

  const { formProps, getFieldProps, isComplete } = useForm({
    fields: {
      integrationPackage: { type: 'radio', required: true },
      bundleAddOns: { type: 'checklist', required: false, value: [BundleAddOnsEnum.BASE] },
      vertical: {
        type: 'dropdown',
        required: true,
      },
      featureAddOns: { type: 'checklist', required: false, value: [AddOn.CALL_INTELLIGENCE] },
      phones: { type: 'checkbox', required: false, value: true },
    },
    onSubmit: (data) => {
      const integration = data.integrationPackage as IntegrationPackagesEnum;
      const requestData: SetDemoLocationCustomizationsRequest = {
        locationIds: filteredLocationIds,
        bundle: computeDemoBundle(integration, data.phones as boolean),
        vertical: data.vertical as Vertical,
        addons: computeDemoAddOns(
          integration,
          data.bundleAddOns as BundleAddOnsEnum[],
          data.featureAddOns as AddOn[]
        ) as AddOn[],
      };
      mutate(requestData);
    },
  });

  useHotkeys(
    DEMO_TOOL_HOTKEY,
    () => {
      if (filteredLocationIds.length > 0) {
        openModal();
      }
    },
    [filteredLocationIds]
  );

  return (
    <Modal asChild {...modalProps} minWidth={600}>
      <form {...formProps}>
        <ContentLoader show={isLoading} />
        <Modal.Header onClose={closeModal}>{t('Demo Features')}</Modal.Header>
        <Modal.Body css={modalContentStyles}>
          <div css={formFieldStyles}>
            <Text size='large' weight='bold' as='label'>
              {t('Select Integration')}
            </Text>
            <RadioCardField {...getFieldProps('integrationPackage')}>
              {integrationPackageOptions.map(({ value, label }) => (
                <RadioCardField.Option key={value} value={value} title={label} />
              ))}
            </RadioCardField>
          </div>
          <div css={formFieldStyles}>
            <Text size='large' weight='bold' as='label'>
              {t('Select Bundles')}
            </Text>
            <ChecklistField
              {...getFieldProps('bundleAddOns')}
              label={t('Select Bundles')}
              showLabel={false}
              css={checkListFieldStyles}
            >
              <ChecklistField.Option
                key={BundleAddOnsEnum.BASE}
                name={BundleAddOnsEnum.BASE}
                disabled
                css={checkboxOptionStyles}
              >
                {t('Base')}
              </ChecklistField.Option>
              {bundleAddOnOptions.map(({ value, label }) => (
                <ChecklistField.Option key={value} name={value} css={checkboxOptionStyles}>
                  {label}
                </ChecklistField.Option>
              ))}
            </ChecklistField>
          </div>
          <div css={[formFieldStyles, verticalFieldStyles]}>
            <Text size='large' weight='bold' as='label'>
              {t('Add Ons')}
            </Text>
            <MultiselectField
              label={''}
              placeholder={t('Select Add Ons')}
              {...getFieldProps('featureAddOns')}
              data-testid='feature-addons-dropdown'
            >
              {featureAddOnOptions.map(({ label, value }) => (
                <MultiselectField.Option key={value} value={`${value}`}>
                  {label}
                </MultiselectField.Option>
              ))}
            </MultiselectField>
          </div>
          <div css={[formFieldStyles, verticalFieldStyles]}>
            <Text size='large' weight='bold' as='label'>
              {t('Vertical')}
            </Text>
            <DropdownField label={t('Vertical')} {...getFieldProps('vertical')} data-testid='vertical-dropdown'>
              {verticalOptions.map(({ label, value }) => (
                <DropdownField.Option key={value} value={`${value}`}>
                  {label}
                </DropdownField.Option>
              ))}
            </DropdownField>
          </div>
          <div css={formFieldStyles}>
            <CheckboxField {...getFieldProps('phones')} label={t('Phones')} />
          </div>
        </Modal.Body>
        <Modal.Footer
          primary={{
            type: 'submit',
            label: t('Done'),
            disabled: !isComplete,
          }}
          secondary={{
            onClick: closeModal,
            label: t('Cancel'),
          }}
        />
      </form>
    </Modal>
  );
};

const legacyBundlePackageOptions = Object.values(LegacyBundlePackagesEnum).map((value) => ({
  value,
  label: LegacyBundlePackagesLabel[value],
}));
const legacyPackageOptions = generateEnumOptions(PackageType, [PackageType.PACKAGE_DEFAULT]);

export const LegacySalesDemoFeatureModal: FC = () => {
  const { t } = useTranslation('salesDev');
  const alerts = useAlert();
  const { modalProps, openModal, closeModal } = useModalControl();
  const { accessibleLocationData, selectedLocationIds } = useAppScopeStore();

  const filteredLocationIds = useMemo(() => {
    return selectedLocationIds.filter((id) => {
      const opsType = accessibleLocationData[id]?.opsType;
      return !!opsType && ALLOWED_LOCATION_TYPE_FOR_DEMO.includes(opsType as OpsType);
    });
  }, [accessibleLocationData, selectedLocationIds]);

  const { mutate, isLoading } = SubscriptionsQueries.useUpdateDemoLocationCustomization({
    onSuccess: () => {
      closeModal();
      reloadPage();
    },
    onError: () => {
      alerts.error(t('Failed to Update'));
    },
  });

  const { formProps, getFieldProps, isComplete } = useForm({
    fields: {
      integrationPackage: { type: 'radio', required: true },
      bundlePackage: { type: 'radio', required: true },
      vertical: {
        type: 'dropdown',
        required: true,
      },
      packageType: { type: 'radio', required: false },
      addons: { type: 'checklist', required: false, value: [] },
    },
    onSubmit: (data) => {
      const requestData: SetDemoLocationCustomizationsRequest = {
        locationIds: filteredLocationIds,
        bundle: getLegacySalesforceBundlePackage({
          integrationPackage: data.integrationPackage as IntegrationPackagesEnum,
          bundlePackage: data.bundlePackage as LegacyBundlePackagesEnum,
          bundleOptional:
            data.packageType === LegacyBundleOptionalsEnum.SOFTWARE_ONLY
              ? LegacyBundleOptionalsEnum.SOFTWARE_ONLY
              : undefined,
        }),
        vertical: data.vertical as Vertical,
        packageTypes:
          data.packageType !== LegacyBundleOptionalsEnum.SOFTWARE_ONLY ? [data.packageType as PackageType] : [],
        addons: data.addons as AddOn[],
      };
      mutate(requestData);
    },
  });

  useHotkeys(
    DEMO_TOOL_HOTKEY,
    () => {
      if (filteredLocationIds.length > 0) {
        openModal();
      }
    },
    [filteredLocationIds]
  );

  return (
    <Modal asChild {...modalProps} minWidth={600}>
      <form {...formProps}>
        <ContentLoader show={isLoading} />
        <Modal.Header onClose={closeModal}>{t('Demo Features')}</Modal.Header>
        <Modal.Body css={modalContentStyles}>
          <div css={formFieldStyles}>
            <Text size='large' weight='bold' as='label'>
              {t('Select Integration')}
            </Text>
            <RadioCardField {...getFieldProps('integrationPackage')}>
              {integrationPackageOptions.map(({ value, label }) => (
                <RadioCardField.Option key={value} value={value} title={label} />
              ))}
            </RadioCardField>
          </div>
          <div css={formFieldStyles}>
            <Text size='large' weight='bold' as='label'>
              {t('Select Bundles')}
            </Text>
            <RadioCardField {...getFieldProps('bundlePackage')}>
              {legacyBundlePackageOptions.map(({ value, label }) => (
                <RadioCardField.Option key={value} value={value} title={label} />
              ))}
            </RadioCardField>
          </div>
          <div css={[formFieldStyles, verticalFieldStyles]}>
            <Text size='large' weight='bold' as='label'>
              {t('Vertical')}
            </Text>
            <DropdownField label={t('Vertical')} {...getFieldProps('vertical')}>
              {verticalOptions.map(({ label, value }) => (
                <DropdownField.Option key={value} value={`${value}`}>
                  {label}
                </DropdownField.Option>
              ))}
            </DropdownField>
          </div>
          <div css={formFieldStyles}>
            <Text size='large' weight='bold' as='label'>
              {t('Packages')}
            </Text>
            <RadioCardField {...getFieldProps('packageType')}>
              {legacyPackageOptions.map(({ value, label }) => (
                <RadioCardField.Option key={value} value={`${value}`} title={label} />
              ))}
            </RadioCardField>
          </div>
        </Modal.Body>
        <Modal.Footer
          primary={{
            type: 'submit',
            label: t('Done'),
            disabled: !isComplete,
          }}
          secondary={{
            onClick: closeModal,
            label: t('Cancel'),
          }}
        />
      </form>
    </Modal>
  );
};

const modalContentStyles = css`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  gap: ${theme.spacing(2)};
`;

const formFieldStyles = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(1)};
`;

const checkListFieldStyles = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(2)};
`;

const checkboxOptionStyles = css`
  margin: 0;
`;

const verticalFieldStyles = css`
  width: 60%;
`;
